import { ActionTree } from 'vuex'
import * as types from './mutation-types'
import RootState from '@vue-storefront/core/types/RootState';
import InventorySourcesState from '../types/InventorySourcesState'
import { InventoryService } from '../services/Inventory'
import { updateLocalStorage, fetchLocalStorage, setMagentoPickupLocation } from '../helpers/localStorage'
import rootStore from '@vue-storefront/core/store'
import { Logger } from '@vue-storefront/core/lib/logger'

const cacheLifetime = 60; // in seconds

const queueDebounceMs = 75
let timeout = null
let queue = []
const inFlightRequests = new Map<string, Promise<void>>()
const queuedResolvers = new Map<string, any[]>()
let activeRequests = 0
var initialized = false;

const unique = (items: string[]) => [...new Set(items.filter(Boolean))]

const resolveQueued = (skus: string[]) => {
  for (const sku of skus) {
    const resolvers = queuedResolvers.get(sku) || []
    for (const resolver of resolvers) resolver.resolve()
    queuedResolvers.delete(sku)
  }
}

const rejectQueued = (skus: string[], error: any) => {
  for (const sku of skus) {
    const resolvers = queuedResolvers.get(sku) || []
    for (const resolver of resolvers) resolver.reject(error)
    queuedResolvers.delete(sku)
  }
}

const refreshCartMethodsAndTotalsInBackground = () => {
  rootStore.dispatch('cart/pullMethods', { forceServerSync: true })
    .then(() => rootStore.dispatch('cart/syncTotals', { forceServerSync: true }))
    .catch((error) => {
      Logger.error(error, 'inventorySources')()
    })
}

const actions: ActionTree<InventorySourcesState, RootState> = {
  async loadInventorySources ({ commit, state }) {
    if (state.loading || state.items.length) {
      return state.items
    }

    const result = await InventoryService.getInventorySources()
    commit(types.INVENTORY_SOURCES_UPDATE_SOURCE, result.items)

    await updateLocalStorage(state)

    commit(types.INVENTORY_SOURCES_LOADING, false)

    return result
  },
  async loadStock ({ commit, state }, skus) {
    const requestedSkus = unique(typeof skus === 'string' ? [skus] : skus || [])
    if (requestedSkus.length === 0) return state.stockItems

    const cachedSkus = new Set(state.stockItems.map(stockItem => stockItem.sku))
    const inFlightPromises: Promise<void>[] = []
    const queuedPromises: Promise<void>[] = []

    for (const sku of requestedSkus) {
      if (cachedSkus.has(sku)) continue

      const inFlight = inFlightRequests.get(sku)
      if (inFlight) {
        inFlightPromises.push(inFlight)
        continue
      }

      queuedPromises.push(new Promise((resolve, reject) => {
        const currentResolvers = queuedResolvers.get(sku) || []
        currentResolvers.push({ resolve, reject })
        queuedResolvers.set(sku, currentResolvers)
      }))

      if (!queue.includes(sku)) {
        queue.push(sku)
      }
    }

    if (queue.length > 0 && !timeout) {
      timeout = setTimeout(async () => {
        const queuedSkus = unique(queue)
        queue = []
        timeout = null

        if (queuedSkus.length === 0) return

        activeRequests += 1
        commit(types.INVENTORY_SOURCES_STOCK_LOADING, true)

        const requestPromise = (async () => {
          const result = await InventoryService.getStocksBySkus(queuedSkus)
          commit(types.INVENTORY_SOURCES_UPDATE_STOCK, (result.items || []).map((stockItem) => {
            stockItem.time = Date.now()
            return stockItem
          }))
          await updateLocalStorage(state)
        })()

        for (const sku of queuedSkus) {
          inFlightRequests.set(sku, requestPromise)
        }

        try {
          await requestPromise
          resolveQueued(queuedSkus)
        } catch (error) {
          rejectQueued(queuedSkus, error)
        } finally {
          for (const sku of queuedSkus) {
            inFlightRequests.delete(sku)
          }
          activeRequests -= 1
          if (activeRequests === 0) {
            commit(types.INVENTORY_SOURCES_STOCK_LOADING, false)
          }
        }
      }, queueDebounceMs)
    }

    await Promise.all([...inFlightPromises, ...queuedPromises])
    return state.stockItems
  },
  async init ({ commit, dispatch }) {
    if (initialized) {
      return;
    }
    initialized = true

    const storage = await fetchLocalStorage()
    if (storage && storage.items.length > 0) {
      commit(types.INVENTORY_SOURCES_UPDATE_SOURCE, storage.items)
      commit(types.INVENTORY_SOURCES_SET_CURRENT, storage.current)
      commit(types.INVENTORY_SOURCES_SET_IS_PICKUP, storage.isPickup)
      commit(types.INVENTORY_SOURCES_UPDATE_STOCK,
        storage.stockItems.filter((s) => {
          return (Date.now() - (s.time + cacheLifetime * 1000)) < 0
        })
      )
    } else {
      return dispatch('loadInventorySources')
    }
  },
  async setCurrent ({ commit, state }, item) {
    commit(types.INVENTORY_SOURCES_SET_CURRENT, item)
    if (state.isPickup) {
      await setMagentoPickupLocation(item && item.pickup_location_code ? item.pickup_location_code : null)
    }
    refreshCartMethodsAndTotalsInBackground()
    await updateLocalStorage(state)
  },
  async setIsPickup ({ commit, state }, isPickup) {
    commit(types.INVENTORY_SOURCES_SET_IS_PICKUP, isPickup)
    await setMagentoPickupLocation(isPickup && state.current && state.current.pickup_location_code ? state.current.pickup_location_code : null)
    refreshCartMethodsAndTotalsInBackground()
    await updateLocalStorage(state)
  }
}

export default actions
